// Copyright (c) Docugami, Inc. All rights reserved.

import { CoachMarkId } from "coachmarks/CoachMarkTypes"
import { DocSetExplorerView } from "components/myContentExplorer/DocSetExplorerView"
import { getProviderForDocSource } from "storage/StorageProviderHelpers"
import { AddDocsExplorerView } from "./AddDocsExplorerView"
import { EmptyDocSetsCoachmark } from "./EmptyDocSetsCoachmark"
import { NavigationGroup, ReviewDocsId, DocSourceId, ExplorerViewType, ExplorerButtonId } from "./ExplorerTypes"
import { ErrorFilesExplorerView } from "./ErrorFilesExplorerView"
import { ExplorerView } from "./ExplorerView"
import { LocalFileSystemExplorerView, RemoteFileSystemExplorerView } from "./FileSystemExplorerView"
import { UnassignedExplorerView } from "./UnassignedExplorerView"
import { useSelector } from "react-redux"
import { AppState } from "store"
import { SamplesCatalogView } from "./SamplesCatalogView"
import { useNavigationService } from "hooks/navigationService"

export interface IExplorerViewFactoryProps {
    group?: NavigationGroup;
    type?: ExplorerViewType;
    itemId?: string;
}

export const ExplorerViewFactory = (props: IExplorerViewFactoryProps): JSX.Element => {
    const organizationId = useSelector((state: AppState) => state.organizations.currentOrganization?.id);
    const { group, itemId, type } = props;

    const navService = useNavigationService();

    if (!organizationId) {
        return <></>;
    }

    if (!!type && !!group && !!itemId) {
        switch (type) {
            case ExplorerViewType.AddDocs: {
                return <AddDocsExplorerView />
            }
            case ExplorerViewType.BrowseSamples: {
                return <SamplesCatalogView
                    onSelection={(id: string) => {
                        navService.navToBrowseSamplesPage(id);
                    }} />
            }
            case ExplorerViewType.LocalDocSource: {
                return <LocalFileSystemExplorerView />
            }
            case ExplorerViewType.DocSet: {
                return <DocSetExplorerView docSetId={itemId} telemetryId={group} />
            }
            case ExplorerViewType.RemoteDocSource: {
                return <RemoteFileSystemExplorerView providerId={getProviderForDocSource(itemId as DocSourceId)!} />
            }
            case ExplorerViewType.Unassigned: {
                const key = itemId as keyof typeof ReviewDocsId
                if (key === ReviewDocsId.Unassigned) {
                    return <UnassignedExplorerView />
                }
                else if (key === ReviewDocsId.Error) {
                    return <ErrorFilesExplorerView />
                }
            }
        }

        throw new Error(`Could not generate explorer view for given navigation context. Group: ${group}, Id: ${itemId}`);
    }
    else if (isValidViewId(itemId)) {
        // Group agnostic views - currently coachmarks are supported as valid explorer views
        let content: JSX.Element;
        switch (itemId) {
            case CoachMarkId.EmptyDocSets: {
                content = <EmptyDocSetsCoachmark />;
                break;
            }

            case ExplorerButtonId.AddDocs:
                return <AddDocsExplorerView />

            case ExplorerButtonId.BrowseSamples:
                return <SamplesCatalogView
                    onSelection={(id: string) => {
                        navService.navToBrowseSamplesPage(id);
                    }}
                />;

            default:
                throw new Error(`Could not generate explorer view for given navigation context. Group: ${group}, Id: ${itemId}`);
        }

        return <ExplorerView header={null} overlays={[]} footer={null} content={content} telemetryId={itemId} />
    }

    return <></>;
}

// Helper for validating a group agnostic view ID
export function isValidViewId(id?: string) {
    return Object.keys(CoachMarkId).includes(id ?? '') || Object.keys(ExplorerButtonId).includes(id ?? '')
}
